import { DateTime } from 'luxon';

export function formatDate(date: Date | string, format = 'EEE DDD t'): string {
  const luxon = date instanceof Date ? DateTime.fromJSDate(date) : DateTime.fromISO(date);
  return luxon.toLocal().toFormat(format);
}

export function formatISODate(date: Date | string): string {
  const luxon = date instanceof Date ? DateTime.fromJSDate(date) : DateTime.fromISO(date, { setZone: true });
  return luxon.toISO() ?? '';
}

export function formatUTCDate(date: Date | string, format = 'EEE DDD t'): string {
  const luxon = date instanceof Date ? DateTime.fromJSDate(date) : DateTime.fromISO(date);
  return luxon.toUTC().toFormat(format);
}

/**
 * Formats a given date in a "friendly" manner, providing a more human-readable
 * output compared to the default `formatDate` function.
 *
 * - If the date is today, it returns "Today at HH:mm:ss".
 * - If the date was yesterday, it returns "Yesterday at HH:mm:ss".
 * - For other dates, it defaults to the provided format (full weekday name with time).
 *
 * This function is designed to enhance user experience by offering context (e.g., "Today" or "Yesterday")
 * for recent dates, rather than always showing a fixed date format.
 *
 * @param {Date | string} date - The date to be formatted, which can be a `Date` object or an ISO string.
 * @param {string} [format="cccc 'at' HH:mm:ss"] - The fallback format to use when the date is neither today nor yesterday.
 * @returns {string} A string representation of the date in a friendly format.
 */
export function formatFriendlyDate(date: Date | string, format = "cccc 'at' HH:mm:ss"): string {
  const luxon = date instanceof Date ? DateTime.fromJSDate(date) : DateTime.fromISO(date);
  const now = DateTime.now();

  if (luxon.hasSame(now, 'day')) {
    return luxon.toFormat("'Today at' HH:mm:ss");
  }

  if (luxon.hasSame(now.minus({ days: 1 }), 'day')) {
    return luxon.toFormat("'Yesterday at' HH:mm:ss");
  }

  if (luxon.hasSame(now, 'month')) {
    return luxon.toFormat("cccc 'at' h:mm a");
  }
  // Default formatting: Full weekday name with time
  return luxon.toFormat(format);
}

// for Teamlookup chats
function safeFloor(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.floor(value) : 0;
}
export function formatLastReplyAgo(date: Date | string): string {
  const luxon = date instanceof Date ? DateTime.fromJSDate(date) : DateTime.fromISO(date);
  const now = DateTime.now();

  if (luxon.hasSame(now, 'day')) {
    return luxon.toFormat("'Today at' HH:mm:ss");
  }

  if (luxon.hasSame(now.minus({ days: 1 }), 'day')) {
    return luxon.toFormat("'Yesterday at' HH:mm:ss");
  }

  const diff = now.diff(luxon, ['years', 'months', 'weeks', 'days']).toObject();

  const years = safeFloor(diff.years);
  if (years >= 1) {
    return `${years} year${years !== 1 ? 's' : ''} ago`;
  }

  const months = safeFloor(diff.months);
  if (months >= 1) {
    return `${months} month${months !== 1 ? 's' : ''} ago`;
  }

  const weeks = safeFloor(diff.weeks);
  if (weeks >= 1) {
    return `Last reply ${weeks} week${weeks !== 1 ? 's' : ''} ago`;
  }

  const days = safeFloor(diff.days);
  if (days >= 1) {
    return `Last reply ${days} day${days !== 1 ? 's' : ''} ago`;
  }
  return 'Last reply just now';
}

function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

export function formatDateByWeekThreshold(date: Date | string): string {
  const dt = date instanceof Date ? DateTime.fromJSDate(date) : DateTime.fromISO(date);
  const now = DateTime.now();

  const diffInWeeks = now.diff(dt, 'weeks').weeks;

  if (diffInWeeks <= 1) {
    return dt.toFormat("cccc 'at' h:mm a");
  }
  const dayWithSuffix = getOrdinalSuffix(dt.day);
  return `${dt.toFormat('LLLL')} ${dayWithSuffix} at ${dt.toFormat('h:mm a')}`;
}
